<template>
  <IonFab
    vertical="bottom"
    horizontal="end"
    slot="fixed"
    :edge="true"
    :class="`custom-ion-fab-menu`"
  >
    <div v-if="menuItems.length > 1">
      <IonFabButton size="small" :edge="true">
        <IonIcon :icon="chevronUpCircle" size="small"></IonIcon>
      </IonFabButton>
      <IonFabList :side="isAppInPortraitMode ? 'start' : 'top'">
        <IonFabButton
          v-for="item in menuItems"
          :key="item.Label"
          size="small"
          @click="onMenuItemClick(item)"
        >
          <IonIcon :icon="item.Icon" size="small"></IonIcon>
        </IonFabButton>
      </IonFabList>
    </div>
    <div v-else class="relative z-0">
      <div
        v-if="isLabelVisible"
        class="fab-halo absolute inset-0 -z-10 rounded-full pointer-events-none"
        aria-hidden="true"
      ></div>
      <IonFabButton size="small" :edge="true" @click="onMenuItemClick(menuItems[0])">
        <IonIcon :icon="menuItems[0].Icon" size="small"></IonIcon>
      </IonFabButton>
      <Transition name="fab-text">
        <div
          v-if="isLabelVisible"
          ref="labelRef"
          data-testid="fab-hint-label"
          class="fab-button-text absolute right-full top-1/2 -translate-y-1/2 me-2 origin-right cursor-pointer text-nowrap text-xxs font-semibold color-text bg-color-light-translucent backdrop-blur-sm border border-color-medium-light shadow-card rounded-(--pill-radius) px-3 py-1.5"
          @click="onMenuItemClick(menuItems[0])"
        >
          {{ menuItems[0].Label }}
        </div>
      </Transition>
    </div>
  </IonFab>
</template>

<script lang="ts">
import { hapticImpact, ImpactStyle } from "@/methods/haptics";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { IonFabButtonMenuItem } from "@/types/global";
import { IonFab, IonFabButton, IonFabList, IonIcon } from "@ionic/vue";
import { chevronUpCircle } from "ionicons/icons";
import { storeToRefs } from "pinia";
import { computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref } from "vue";

// Component-identity timings, deliberately outside the --motion-duration ladder:
// let the screen/modal transition settle before the hint unfurls, then keep it up
// only until the user interacts or the window elapses.
const LABEL_SHOW_DELAY_MS = 600;
const LABEL_VISIBLE_MS = 4000;

export default defineComponent({
  name: "CustomIonFabMenu",
  components: { IonFab, IonFabButton, IonFabList, IonIcon },
  props: {
    menuItems: { type: Array as PropType<Array<IonFabButtonMenuItem>>, required: true },
    showButtonText: { type: Boolean, default: false },
  },
  setup(props) {
    const appSettingsStore = useAppSettingsStore();
    const { AppScreenOrientation, IsNativePlatform } = storeToRefs(appSettingsStore);
    const isLabelVisible = ref(false);
    const labelRef = ref<HTMLElement | null>(null);
    let showTimeoutId: number | undefined;
    let hideTimeoutId: number | undefined;

    const isAppInPortraitMode = computed(() => {
      if (!AppScreenOrientation.value || !IsNativePlatform.value) {
        return false;
      }
      return !AppScreenOrientation.value.startsWith("portrait");
    });

    function dismissLabel() {
      window.clearTimeout(hideTimeoutId);
      window.removeEventListener("pointerdown", onGlobalPointerDown, true);
      isLabelVisible.value = false;
    }

    // A user already tapping/scrolling doesn't need the hint — first interaction
    // anywhere dismisses it early. Taps on the label itself are exempt so the
    // dismissal doesn't swallow the label's own click.
    function onGlobalPointerDown(event: Event) {
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      if (labelRef.value && path.includes(labelRef.value)) {
        return;
      }
      dismissLabel();
    }

    onMounted(() => {
      if (!props.showButtonText) {
        return;
      }
      showTimeoutId = window.setTimeout(() => {
        isLabelVisible.value = true;
        hideTimeoutId = window.setTimeout(dismissLabel, LABEL_VISIBLE_MS);
        window.addEventListener("pointerdown", onGlobalPointerDown, true);
      }, LABEL_SHOW_DELAY_MS);
    });

    onBeforeUnmount(() => {
      window.clearTimeout(showTimeoutId);
      dismissLabel();
    });

    async function onMenuItemClick(item: IonFabButtonMenuItem) {
      dismissLabel();
      await hapticImpact(ImpactStyle.Light);
      item.onClick();
    }

    return {
      chevronUpCircle,
      isAppInPortraitMode,
      isLabelVisible,
      labelRef,
      onMenuItemClick,
    };
  },
});
</script>
<style lang="scss" scoped>
// scoped-defer: bucket-c-candidate: nested SCSS rules — child-element gluing, hoist to @layer components if reused
.custom-ion-fab-menu {
  margin-bottom: calc(var(--fab-button-height) + var(--ion-safe-area-bottom));
  & .fab-halo {
    background: var(--ion-color-primary);
    /* 700ms is a component-identity timing — the one-shot attention ping that
       accompanies the hint, deliberately outside the --motion-duration ladder. */
    animation: fab-halo-ping 700ms var(--motion-ease-out) forwards;
  }
  // Tooltip tail: rotated square flush with the pill's FAB-side edge, sharing its
  // surface and hairline border so the hint reads as attached to the button.
  & .fab-button-text::after {
    content: "";
    position: absolute;
    top: 50%;
    right: -0.25rem;
    width: 0.5rem;
    height: 0.5rem;
    transform: translateY(-50%) rotate(45deg);
    background: var(--color-light-translucent);
    border-top: 1px solid var(--color-medium-light);
    border-right: 1px solid var(--color-medium-light);
  }
}
// Vue <Transition name="fab-text"> hooks — the hint unfurls out of the FAB edge and
// tucks back into it. Vertical centering lives on the translate property (Tailwind
// -translate-y-1/2), so animating transform here doesn't disturb it.
.fab-text-enter-active {
  animation: fab-text-unfurl var(--motion-duration-slow) var(--motion-ease-out);
}
.fab-text-leave-active {
  animation: fab-text-tuck var(--motion-duration-slow) var(--motion-ease-in);
}
@keyframes fab-text-unfurl {
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}
@keyframes fab-text-tuck {
  from {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateX(10px) scale(0.9);
  }
}
@keyframes fab-halo-ping {
  from {
    opacity: 0.5;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.7);
  }
}
@media (prefers-reduced-motion: reduce) {
  .fab-text-enter-active,
  .fab-text-leave-active {
    animation: none;
  }
  .custom-ion-fab-menu .fab-halo {
    animation: none;
    visibility: hidden;
  }
}
</style>
